import { useState, useEffect, useRef } from "react";

const INITIAL_WEEKLY = 64518;
const INITIAL_TODAY = 9714;
const INITIAL_ACTIVE = 6;
const MAX_WEEKLY = 67999;

const tickerItems = [
  { city: "Tampa, FL", event: "New listing detected", agent: "Marcus R.", icon: "ri-home-4-line", accentClass: "text-primary" },
  { city: "Austin, TX", event: "New listing detected", agent: "Diana L.", icon: "ri-home-4-line", accentClass: "text-secondary" },
  { city: "Phoenix, AZ", event: "New listing detected", agent: "Steve B.", icon: "ri-home-4-line", accentClass: "text-primary" },
  { city: "Jacksonville, FL", event: "New listing detected", agent: "Carla M.", icon: "ri-home-4-line", accentClass: "text-secondary" },
  { city: "Nashville, TN", event: "New listing detected", agent: "Ray J.", icon: "ri-home-4-line", accentClass: "text-primary" },
  { city: "Charlotte, NC", event: "New listing detected", agent: "Nicole K.", icon: "ri-home-4-line", accentClass: "text-secondary" },
  { city: "Denver, CO", event: "New listing detected", agent: "Tom H.", icon: "ri-home-4-line", accentClass: "text-primary" },
  { city: "Atlanta, GA", event: "New listing detected", agent: "Amy W.", icon: "ri-home-4-line", accentClass: "text-secondary" },
  { city: "Las Vegas, NV", event: "New listing detected", agent: "Erik S.", icon: "ri-home-4-line", accentClass: "text-primary" },
  { city: "Dallas, TX", event: "New listing detected", agent: "Donna P.", icon: "ri-home-4-line", accentClass: "text-secondary" },
  { city: "Miami, FL", event: "New listing detected", agent: "Chad F.", icon: "ri-home-4-line", accentClass: "text-primary" },
  { city: "Seattle, WA", event: "New listing detected", agent: "Sarah T.", icon: "ri-home-4-line", accentClass: "text-secondary" },
  { city: "Orlando, FL", event: "New listing detected", agent: "James V.", icon: "ri-home-4-line", accentClass: "text-primary" },
  { city: "Scottsdale, AZ", event: "New listing detected", agent: "Olivia R.", icon: "ri-home-4-line", accentClass: "text-secondary" },
];

// Double the list for seamless infinite scroll
const doubledItems = [...tickerItems, ...tickerItems];

function useTickingNumber(initial: number, minIncrement: number, maxIncrement: number, intervalMs: number, max?: number) {
  const [count, setCount] = useState(initial);
  const [flashing, setFlashing] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      const inc = Math.floor(Math.random() * (maxIncrement - minIncrement + 1)) + minIncrement;
      setCount((prev) => {
        const next = prev + inc;
        return max !== undefined ? Math.min(max, next) : next;
      });
      setFlashing(true);
      setTimeout(() => setFlashing(false), 500);
    }, intervalMs);
    return () => clearInterval(timer);
  }, [minIncrement, maxIncrement, intervalMs, max]);

  return { count, flashing };
}

function useFluctuatingNumber(initial: number, min: number, max: number, intervalMs: number) {
  const [count, setCount] = useState(initial);

  useEffect(() => {
    const timer = setInterval(() => {
      const delta = Math.floor(Math.random() * 3) - 1;
      setCount((prev) => Math.max(min, Math.min(max, prev + delta)));
    }, intervalMs);
    return () => clearInterval(timer);
  }, [min, max, intervalMs]);

  return count;
}

export default function LiveTickerSection() {
  const { count: weekly, flashing: weeklyFlash } = useTickingNumber(INITIAL_WEEKLY, 1, 4, 4200, MAX_WEEKLY);
  const { count: today, flashing: todayFlash } = useTickingNumber(INITIAL_TODAY, 0, 1, 7800);
  const activeCount = useFluctuatingNumber(INITIAL_ACTIVE, 4, 9, 3500);

  const tickerRef = useRef<HTMLDivElement>(null);

  return (
    <section className="bg-navy w-full overflow-hidden">
      <style>{`
        @keyframes ticker-scroll {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .live-ticker-track {
          animation: ticker-scroll 52s linear infinite;
          will-change: transform;
        }
        .live-ticker-track:hover {
          animation-play-state: paused;
        }
        @keyframes count-pop {
          0%   { transform: scale(1); }
          40%  { transform: scale(1.08); color: #f1663a; }
          100% { transform: scale(1); }
        }
        .count-pop {
          animation: count-pop 0.45s ease-out;
        }
      `}</style>

      {/* Top: main counter row */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-5 lg:py-6 flex flex-col lg:flex-row items-center gap-4 lg:gap-0 justify-between">

        {/* LIVE badge + label */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 bg-green-500/15 border border-green-500/30 rounded-full px-3.5 py-1.5">
            <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse flex-shrink-0" />
            <span className="text-green-300 text-xs font-bold uppercase tracking-widest">Live</span>
          </div>
          <p className="text-white/70 text-sm font-medium whitespace-nowrap">
            New Listings This Week
          </p>
        </div>

        {/* Main counter */}
        <div className="flex items-baseline gap-3">
          <span
            key={weekly}
            className={`text-primary font-extrabold leading-none count-pop`}
            style={{ fontSize: "clamp(38px, 4vw, 54px)" }}
          >
            {weekly.toLocaleString()}
          </span>
          <div className="flex items-center gap-1.5 mb-1">
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-up-line text-green-400 text-sm" />
            </span>
            <span className="text-green-400 text-xs font-semibold">updating live</span>
          </div>
        </div>

        {/* Sub-stats */}
        <div className="grid grid-cols-3 gap-2 sm:flex sm:items-center sm:gap-8 w-full sm:w-auto">
          <div className="text-center">
            <p
              key={today}
              className={`text-white font-bold text-lg md:text-xl leading-tight ${todayFlash ? "count-pop" : ""}`}
            >
              {today.toLocaleString()}
            </p>
            <p className="text-white/40 text-[10px] md:text-xs mt-0.5">new Listings ready to trigger</p>
          </div>

          <div className="hidden sm:block w-px h-8 bg-white/10" />

          <div className="text-center">
            <p className="font-bold text-lg md:text-xl leading-tight" style={{ color: "#19a2fd" }}>
              {activeCount}
            </p>
            <p className="text-white/40 text-[10px] md:text-xs mt-0.5">just went active</p>
          </div>

          <div className="hidden sm:block w-px h-8 bg-white/10" />

          <div className="text-center">
            <p className="text-white font-bold text-lg md:text-xl leading-tight">National</p>
            <p className="text-white/40 text-[10px] md:text-xs mt-0.5">Primary Market</p>
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="w-full h-px bg-white/8" />

      {/* Bottom: scrolling transaction ticker */}
      <div className="relative py-3 overflow-hidden" ref={tickerRef}>
        {/* Left fade */}
        <div className="absolute left-0 top-0 bottom-0 w-20 z-10 pointer-events-none"
          style={{ background: "linear-gradient(to right, #0b1f33 0%, transparent 100%)" }} />
        {/* Right fade */}
        <div className="absolute right-0 top-0 bottom-0 w-20 z-10 pointer-events-none"
          style={{ background: "linear-gradient(to left, #0b1f33 0%, transparent 100%)" }} />

        <div className="live-ticker-track flex items-center gap-0">
          {doubledItems.map((item, idx) => (
            <div
              key={idx}
              className="flex items-center gap-2.5 flex-shrink-0 px-5 border-r border-white/8"
            >
              <div className="w-6 h-6 flex items-center justify-center bg-white/8 rounded-md flex-shrink-0">
                <i className={`${item.icon} ${item.accentClass} text-xs`} />
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-white/80 text-xs font-semibold whitespace-nowrap">{item.event}</span>
                <span className="text-white/30 text-xs">·</span>
                <span className="text-white/50 text-xs whitespace-nowrap">{item.city}</span>
                <span className="text-white/30 text-xs">·</span>
                <span className={`text-xs font-medium whitespace-nowrap ${item.accentClass}`}>{item.agent}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
